import { useMemo } from 'react'
import { useQuery, gql } from '@apollo/client'

import { filterRows, rankKeys, totalsBy, toOptions } from './activityReport'

export const USER_ACTIVITY_REPORT = gql`query UserActivityReport($dateAddedGte: Date!, $dateAddedLte: Date!) {
    userActivityReport(dateAddedGte: $dateAddedGte, dateAddedLte: $dateAddedLte) {
        userId
        userName
        branchName
        action
        eventDate
        objectsCount
        eventsCount
    }
}`

/**
 * Groups and their members, for the group filter.
 *
 * Uses userGroupMemberships rather than allGroups { userSet }: userSet is a
 * nested connection, which is re-evaluated per group and cost two queries each,
 * so this dropdown was firing a hundred queries on a few dozen groups. The flat
 * field is two queries however many groups exist.
 *
 * Members are matched to report rows by id, never by name: display names are not
 * unique, and the report's name formula and `UserNode.resolve_name` do not agree
 * for a user who has a last name but no first name.
 */
export const ACTIVITY_REPORT_GROUPS = gql`query ActivityReportGroups {
    userGroupMemberships {
        id
        name
        userIds
    }
}`

/**
 * Fetches the activity report once for a date range and exposes it to every tab.
 *
 * `actions` is deliberately not sent to the server: filtering client-side keeps
 * the response instant and keeps the server-side cache key down to the date
 * range alone (the resolver's cache is keyed on its kwargs).
 */
const useActivityReport = ({ startDate, endDate, filters }) => {
    const { data, loading, error } = useQuery(USER_ACTIVITY_REPORT, {
        variables: { dateAddedGte: startDate, dateAddedLte: endDate },
    })

    // Groups do not depend on the date range, so this runs once and is served
    // from the Apollo cache thereafter.
    const { data: groupData } = useQuery(ACTIVITY_REPORT_GROUPS)

    return useMemo(() => {
        const rows = data?.userActivityReport || []

        const groupOptions = (groupData?.userGroupMemberships || [])
            .map((group) => ({
                value: group.id,
                label: group.name,
                userIds: (group.userIds || []).map(String),
            }))
            // A group with no members would silently filter everything away, so
            // it is not offered as a choice.
            .filter((group) => group.userIds.length)

        // Options and colour rankings come from the *unfiltered* rows, so
        // narrowing a filter never repaints the surviving series or empties the
        // dropdown you are filtering with.
        const userOptions = toOptions(rankKeys(totalsBy(rows, (row) => row.userName)))
        const branchOptions = toOptions(rankKeys(totalsBy(rows, (row) => row.branchName)))

        return {
            loading,
            error,
            rows,
            filteredRows: filterRows(rows, filters),
            userOptions,
            branchOptions,
            groupOptions,
        }
    }, [data, groupData, loading, error, filters])
}

export default useActivityReport
